import { createSlice } from "@reduxjs/toolkit";
import {
  deleteFrameAgreement,
  fetchContracts,
  fetchSummary,
  fetchContractCount,
  fetchAgreementRules,
} from "../Services/ContractThunk";
import { frameAgreementReducers, frameAgreementState } from "./FrameAgreement";
import {
  agreementTemplateReducers,
  agreementTemplateState,
} from "./AgreementTemplate";

function resolveAgreementRules(rulesData: any): Record<string, any> {
  if (!rulesData?.rules || !rulesData?.meta?.states) {
    return {};
  }
  const { rules: agreementTypes, meta } = rulesData;
  const states: Record<string, any> = meta.states;
  const resolved: Record<string, any> = {};
  Object.entries(agreementTypes).forEach(([agreementType, subTypes]) => {
    resolved[agreementType] = {};
    Object.entries(subTypes as Record<string, any>).forEach(
      ([subType, deptCodes]) => {
        resolved[agreementType][subType] = {};
        Object.entries(deptCodes as Record<string, string>).forEach(
          ([dept, stateCode]) => {
            resolved[agreementType][subType][dept] = states[stateCode] ??
              states["default"] ?? { submit_value: stateCode };
          },
        );
      },
    );
  });
  return resolved;
}

// Import the necessary types for the state
interface ContractsState {
  loading: boolean;
  error: string | null;
  data: {
    summary: {
      loading: boolean;
      data: any[];
    };
    agreementSummary: {
      loading: boolean;
      data: any[];
    };
    // Define the structure of frameAgreement data
    frameAgreement: {
      loading: boolean;
      selectedTab: string;
      searchKey?: string;
      deleteRow: {};
      editRow: {};
      selectedRow?: any[];
      isRefreshing: boolean;
      data: any[];
      total_count: number;
      pagination: {
        totalCount: number;
        currentPage: number;
        itemsPerPage: number;
        autoRefresh: boolean;
      };
      formData: {
        frameAgreementType: string;
        scrmId: string;
        agreementType: string;
        agreementSubType: string;
        contractualSituation: string;
        caseName: string;
        market: string;
        caseSummary: string;
        coOwners: string;
        agreementTerritory: string;
        accountOptionsList: any;
        selectedAccountInfo: any;
      };
      countryList: any[];
      isProcessing: boolean;
      isLoaded: boolean;
      opportunityData: any;
      hasScrmDefaultData: boolean;
      departmentApproval: {};
    };
    agreementTemplate: {
      loading: boolean;
      selectedTab: string;
      searchKey?: string;
      deleteRow: {};
      editRow: {};
      selectedRow?: any[];
      isRefreshing: boolean;
      data: any[];
      total_count: number;
      pagination: {
        totalCount: number;
        currentPage: number;
        itemsPerPage: number;
        autoRefresh: boolean;
      };

      formData: {
        createAs: string;
        templateName: string;
        url: string;
        originalUrl: string;
        agreementType: string;
        agreementSubType: string;
        validFrom: string;
        validTo: string;
        market: string;
        country: string;
        esignStatus: string;
        // add other template-specific fields here if needed
      };

      countryList: any[];
      isProcessing: boolean;
      isLoaded: boolean;
      opportunityData: any;
      hasScrmDefaultData: boolean;
      selectedAgreementDetails: any;
      esignAgreementId: string;
      esignAgreementStatus: any;
      previewUrl: string;
      previewFile: any;
    };
    agreementRules: {
      loading: boolean;
      data: any;
      resolvedRules: Record<string, any>;
      standardDeviationRule: any;
      standardDeviationSelected: Record<string, any>;
    };
  };
}

const initialState: ContractsState = {
  loading: false,
  error: null,
  data: {
    summary: {
      loading: false,
      data: [],
    },
    agreementSummary: {
      loading: false,
      data: [],
    },
    frameAgreement: {
      selectedTab: "active_agreements",
      searchKey: "",
      deleteRow: {},
      editRow: {},
      selectedRow: [],
      loading: false,
      isRefreshing: false,
      data: [],
      total_count: 0,
      pagination: {
        totalCount: 0,
        currentPage: 1,
        itemsPerPage: 10,
        autoRefresh: false,
      },
      departmentApproval: {},
      ...frameAgreementState,
    },
    agreementTemplate: {
      loading: false,
      selectedTab: "all_agreement_templates",
      searchKey: "",
      deleteRow: {},
      editRow: {},
      selectedRow: [],
      isRefreshing: false,
      data: [],
      total_count: 0,
      pagination: {
        totalCount: 0,
        currentPage: 1,
        itemsPerPage: 10,
        autoRefresh: false,
      },
      countryList: [],
      opportunityData: null,
      hasScrmDefaultData: false,
      ...agreementTemplateState,
    },
    agreementRules: {
      loading: false,
      data: null,
      resolvedRules: {},
      standardDeviationRule: null,
      standardDeviationSelected: {},
    },
  },
};

const contractsSlice = createSlice({
  name: "contracts",
  initialState,
  reducers: {
    setFrameAgreementIsRefreshing(state, action) {
      state.data.frameAgreement.isRefreshing = action.payload;
    },
    setAgreementTemplateIsRefreshing(state, action) {
      state.data.agreementTemplate.isRefreshing = action.payload;
    },
    setFrameAgreementAutoRefreshToggle(state, action) {
      state.data.frameAgreement.pagination.autoRefresh = action.payload;
    },
    setTabSelected(state, action) {
      state.data.frameAgreement.selectedTab = action.payload;
      state.data.frameAgreement.pagination["currentPage"] = 1;
    },
    setAgreementTabSelected(state, action) {
      // Update the selected tab
      state.data.agreementTemplate.selectedTab = action.payload;
      state.data.agreementTemplate.pagination["currentPage"] = 1;
    },
    setFrameAgreementSelectedRow(state, action) {
      state.data.frameAgreement.selectedRow = action.payload;
    },
    setFrameAgreementDeleteRow(state, action) {
      state.data.frameAgreement.deleteRow = action.payload;
    },
    setFrameAgreementEditRow(state, action) {
      state.data.frameAgreement.editRow = action.payload;
    },
    setFrameAgreementSearch(state, action) {
      state.data.frameAgreement.searchKey = action.payload || "";
    },
    setContractListProps(
      state,
      action: { payload: { key: keyof ContractsState; value: any } },
    ) {
      const { key, value } = action.payload;
      state[key] = value as never;
    },
    setAccountList(state, action) {
      state.data.frameAgreement.formData.accountOptionsList = action.payload;
    },
    setSelectedAccountInfo(state, action) {
      state.data.frameAgreement.formData.selectedAccountInfo = action.payload;
    },
    setContractData(state, action) {
      state.data.frameAgreement.data = action.payload;
    },
    setAgreementTemplateData(state, action) {
      state.data.agreementTemplate.data = action.payload;
    },
    updateFrameAgreementPagination(
      state,
      action: {
        payload: {
          key: keyof ContractsState["data"]["frameAgreement"]["pagination"];
          value: any;
        };
      },
    ) {
      state.data.frameAgreement.pagination[action?.payload?.key] = (action
        .payload?.value || 0) as never;
      if (action?.payload?.key === "itemsPerPage") {
        state.data.frameAgreement.pagination.currentPage = 1;
      }
      if (action?.payload?.key !== "totalCount") {
        state.data.frameAgreement.isRefreshing = true;
      }
    },
    updateAgreementTemplatePagination(
      state,
      action: {
        payload: {
          key: keyof ContractsState["data"]["agreementTemplate"]["pagination"];
          value: any;
        };
      },
    ) {
      state.data.agreementTemplate.pagination[action?.payload?.key] = (action
        .payload?.value || 0) as never;
      if (action?.payload?.key === "itemsPerPage") {
        state.data.agreementTemplate.pagination.currentPage = 1;
      }
      if (action?.payload?.key !== "totalCount") {
        state.data.agreementTemplate.isRefreshing = true;
      }
    },
    updateDepartmentApproval(state, action) {
      const { approval } = action.payload;
      state.data.frameAgreement.departmentApproval = approval;
    },
    // Update Standard Deviation Rule Change
    updateStandardDeviationRuleChange(state, action) {
      const { ruleId, stateKey, value, type } = action.payload;
      if (type === "reset") {
        state.data.agreementRules.standardDeviationSelected = Object.keys(state.data.agreementRules.standardDeviationSelected).reduce((acc: Record<string, any>, key) => {
          acc[key] = {
            selected: "yes",
            option: "",
            note: ""
          };
          return acc;
        }, {});
      } else if (state.data.agreementRules.standardDeviationSelected[ruleId]) {
        state.data.agreementRules.standardDeviationSelected[ruleId][stateKey] = value;
      }
    },
    // Restore all deviation rules from saved agreementDeviation in one pass — avoids
    // dispatching N × 3 individual actions when loading a saved agreement.
    restoreStandardDeviationRules(state, action) {
      const deviations: Array<{ ruleId: string; selected: string; option: string; note: string }> = action.payload;
      const selected = state.data.agreementRules.standardDeviationSelected;
      deviations.forEach(({ ruleId, selected: sel, option, note }) => {
        if (selected[ruleId]) {
          selected[ruleId].selected = sel;
          selected[ruleId].option = option;
          selected[ruleId].note = note;
        }
      });
    },
    // Batch-update all rules when the "all disabled" toggle changes — avoids
    // dispatching N × 3 individual actions inside a useEffect loop.
    setAllRulesDisabled(state, action) {
      const { isDisabled } = action.payload as { isDisabled: boolean };
      const selected = state.data.agreementRules.standardDeviationSelected;
      Object.keys(selected).forEach((key) => {
        selected[key].selected = isDisabled ? "yes" : "no";
        if (isDisabled) {
          selected[key].option = "";
          selected[key].note = "";
        }
      });
    },
    ...frameAgreementReducers,
    ...agreementTemplateReducers,
  },
  extraReducers: (builder) => {
    builder
      // Summary
      .addCase(fetchSummary.pending, (state, action) => {
        if (action?.meta?.arg === "esign") {
          state.data.agreementSummary.loading = true;
        } else {
          state.data.summary.loading = true;
        }
      })
      .addCase(fetchSummary.fulfilled, (state, action) => {
        if (action?.meta?.arg === "esign") {
          state.data.agreementSummary.data =
            action?.payload &&
            "data" in action.payload &&
            Array.isArray(action.payload.data)
              ? action.payload.data
              : [];
          state.data.agreementSummary.loading = false;
        } else {
          state.data.summary.data =
            action?.payload &&
            "data" in action.payload &&
            Array.isArray(action.payload.data)
              ? action.payload.data
              : [];
          state.data.summary.loading = false;
        }
      })
      .addCase(fetchSummary.rejected, (state, action) => {
        if (action?.meta?.arg === "esign") {
          state.data.agreementTemplate.loading = true;
        } else {
          state.data.frameAgreement.loading = true;
        }
      })
      // Contracts
      .addCase(fetchContracts.pending, (state, action) => {
        if (action?.meta?.arg?.type === "esign") {
          state.data.agreementTemplate.loading = true;
        } else {
          state.data.frameAgreement.loading = true;
        }
      })
      .addCase(fetchContracts.fulfilled, (state, action) => {
        const isEsignRequested = action?.meta?.arg?.type || "";
        if (!isEsignRequested) {
          state.data.frameAgreement.data =
            action?.payload?.["data"]?.contracts || [];
        } else {
          state.data.agreementTemplate.data =
            action?.payload?.["data"]?.contracts || [];
        }
        // (action?.payload && 'data' in action.payload && action?.payload?.data?.total_count) || 0;
        state.data.frameAgreement.loading = false;
        state.data.agreementTemplate.loading = false;
        // Reset isRefreshing flag when fetch is successful
        state.data.frameAgreement.isRefreshing = false;
        state.data.agreementTemplate.isRefreshing = false;
      })
      .addCase(fetchContracts.rejected, (state) => {
        // Ensure retry triggers work after backend errors.
        state.data.frameAgreement.loading = false;
        state.data.agreementTemplate.loading = false;
        state.data.frameAgreement.isRefreshing = false;
        state.data.agreementTemplate.isRefreshing = false;
      })
      .addCase(fetchContractCount.pending, (state) => {})
      .addCase(fetchContractCount.fulfilled, (state, action) => {
        const isEsignRequested =
          action.meta?.arg?.headers?.requested_info === "esign";
        if (isEsignRequested) {
          if (state.data?.agreementTemplate) {
            state.data.agreementTemplate.total_count = action.payload;
            state.data.agreementTemplate.pagination.totalCount = action.payload;
          }
        } else {
          if (state.data?.frameAgreement) {
            state.data.frameAgreement.total_count = action.payload;
            state.data.frameAgreement.pagination.totalCount = action.payload;
          }
        }
      })
      .addCase(fetchContractCount.rejected, (state, action) => {
        // Preserve the last known counts so pagination does not disappear
        // when count API fails for a specific filter request.
        state.data.frameAgreement.loading = false;
        state.data.agreementTemplate.loading = false;
      })
      // Delete Frame Agreement
      .addCase(deleteFrameAgreement.pending, (state) => {})
      .addCase(deleteFrameAgreement.fulfilled, (state, action) => {
        // const contractId = action.payload;
        state.data.frameAgreement.isRefreshing = true;
        state.data.agreementTemplate.isRefreshing = true;
      })
      .addCase(deleteFrameAgreement.rejected, (state) => {})
      // Fetch Agreement Rules
      .addCase(fetchAgreementRules.pending, (state) => {
        state.data.agreementRules.loading = true;
      })
      .addCase(fetchAgreementRules.fulfilled, (state, action) => {
        state.data.agreementRules.loading = false;
        const {deviationData, agreementRuleData} = action.payload;
        state.data.agreementRules.data = agreementRuleData;
        state.data.agreementRules.resolvedRules = resolveAgreementRules(
          agreementRuleData,
        );
        state.data.agreementRules.standardDeviationRule = deviationData;
        // Initialize standardDeviationSelected with default values
        const initialSelected: Record<number, any> = {};
        deviationData?.forEach((rule: any) => {
          initialSelected[rule.id] = {
            selected: "yes",
            option: "",
            note: "",
          };
        });
        state.data.agreementRules.standardDeviationSelected = initialSelected;
      })
      .addCase(fetchAgreementRules.rejected, (state) => {
        state.data.agreementRules.loading = false;
        state.data.agreementRules.data = null;
      });
  },
});

export const {
  setFrameAgreementIsRefreshing,
  setAgreementTemplateIsRefreshing,
  setTabSelected,
  setFrameAgreementSelectedRow,
  setFrameAgreementEditRow,
  setFrameAgreementDeleteRow,
  setFrameAgreementAutoRefreshToggle,
  setFrameAgreementSearch,
  updateFrameAgreementPagination,
  updateAgreementTemplatePagination,
  setContractListProps,
  setContractData,
  setAgreementTemplateData,
  setAccountList,
  setSelectedAccountInfo,
  setAgreementTabSelected,
  // Frame Agreement
  setFrameAgreementFormData,
  setFrameAgreementFormDataByKey,
  setFrameAgreementProcessingState,
  setFrameAgreementLoadedState,
  setFrameAgreementOpportunityData,
  setFrameAgreementScrmDefault,
  setFrameAgreementCountryList,
  setFrameAgreementNotFound,
  setFrameAgreementDeletedFiles,
  setFrameAgreementAttachedFiles,
  updateDepartmentApproval,
  updateStandardDeviationRuleChange,
  restoreStandardDeviationRules,
  setAllRulesDisabled,

  // Agreement Template
  setAgreementTemplateFormData,
  setAgreementTemplateFormDataByKey,
  setAgreementTemplateProcessingState,
  setAgreementTemplateLoadedState,
  setAgreementTemplateSelectedAgreementDetails,
  setAgreementTemplateEsignAgreementId,
  setAgreementTemplateEsignAgreementStatus,
  setAgreementTemplatePreviewUrl,
  setAgreementTemplatePreviewFile,
  setAgreementTemplateAttachedFiles,
  setAgreementTemplateDeletedFiles,
  resetAgreementUserDetails,
  clearAgreementTemplatePreview,
  setAgreementTemplateMode,
  setFrameAgreementDropdownList,
} = contractsSlice.actions;

export default contractsSlice.reducer;
